import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

export const DEFAULT_LIST_PAGE_SIZE = 20;

/** Altezza riga tabella desktop (allineata a py-2.5 + text-[13px]). */
export const LIST_TABLE_ROW_HEIGHT_CLASS = 'h-[42px]';

interface ListPaginationProps {
  page: number;
  totalPages: number;
  totalItems: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
  /** Optional status line centered between range text and page controls (e.g. background sync). */
  centerHint?: string;
  /** When true, total in "of N" is shown as "N+" (more items on server). */
  totalHasMore?: boolean;
  className?: string;
}

export function ListPagination({
  page,
  totalPages,
  totalItems,
  pageSize = DEFAULT_LIST_PAGE_SIZE,
  onPageChange,
  disabled = false,
  centerHint,
  totalHasMore = false,
  className,
}: ListPaginationProps) {
  const { t } = useLanguage();

  if (totalItems === 0) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const totalLabel = totalHasMore ? `${totalItems}+` : String(totalItems);

  return (
    <div className={cn('flex h-full w-full min-w-0 items-center justify-between gap-1 sm:gap-2', className)}>
      <p className="min-w-0 truncate text-[10px] leading-none text-muted-foreground sm:text-xs md:text-sm md:whitespace-nowrap md:shrink-0">
        <span className="sm:hidden tabular-nums">
          {from}–{to}/{totalLabel}
        </span>
        <span className="hidden sm:inline">
          {t('pagination.showing', { from, to, total: totalLabel })}
        </span>
      </p>
      {centerHint ? (
        <p className="order-3 hidden min-w-0 flex-1 truncate px-1 text-center text-[10px] leading-none text-muted-foreground sm:order-none sm:block sm:text-xs md:px-2">
          {centerHint}
        </p>
      ) : (
        <div className="hidden min-w-0 flex-1 sm:block" aria-hidden />
      )}
      <div className="flex shrink-0 items-center gap-0.5 sm:gap-2">
        <span className="whitespace-nowrap text-[10px] leading-none text-muted-foreground tabular-nums sm:text-xs md:text-sm">
          <span className="sm:hidden">{page}/{totalPages}</span>
          <span className="hidden sm:inline">{t('pagination.pageOf', { page, totalPages })}</span>
        </span>
        <Button
          variant="outline"
          size="sm"
          className="h-7 w-7 shrink-0 p-0 sm:w-auto sm:gap-1 sm:px-2"
          disabled={disabled || !canPrev}
          aria-label={t('pagination.previous')}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          <span className="hidden sm:inline text-xs">{t('pagination.previous')}</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-7 w-7 shrink-0 p-0 sm:w-auto sm:gap-1 sm:px-2"
          disabled={disabled || !canNext}
          aria-label={t('pagination.next')}
          onClick={() => onPageChange(page + 1)}
        >
          <span className="hidden sm:inline text-xs">{t('pagination.next')}</span>
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
